/**
 * Site Status Constants
 *
 * Centralized constants for site lifecycle status values. These MUST match
 * the Postgres `site_lifecycle_status` enum exactly:
 *
 *   draft       — Site row exists; no deploy in flight. Initial state.
 *   publishing  — fork-site or publish-site workflow is running.
 *   live        — Site is deployed and serving traffic.
 *   failed      — Most recent workflow run failed; `lastError` columns hold
 *                 context. User can retry from the site detail page.
 *   suspended   — Subscription canceled; site serves the suspension
 *                 placeholder page. Sweeps to `destroying` → `offline`
 *                 after 30 days.
 *   destroying  — Destroy workflow is running (cron-driven offline sweep).
 *   offline     — Site has been torn down (Pages project + DNS removed). Row
 *                 and content preserved so user can re-publish later.
 *   deleted     — Soft-deleted by the user. Row preserved for audit trail;
 *                 filtered out of all list queries; subdomain freed for reuse.
 *                 Never appears in the UI.
 *
 * `publishing` and `destroying` are transitional. The frontend polls site
 * data while in those states; the webhook handler advances the status as
 * workflow callbacks arrive.
 */

export const SITE_STATUS = Object.freeze({
  /** Site row exists; no deploy in flight. */
  DRAFT: 'draft',
  /** Fork or publish workflow is running. */
  PUBLISHING: 'publishing',
  /** Site is deployed and serving traffic. */
  LIVE: 'live',
  /** Most recent workflow run failed; lastError columns hold context. */
  FAILED: 'failed',
  /** Subscription canceled; site serves the suspension placeholder. */
  SUSPENDED: 'suspended',
  /** Destroy workflow is running. */
  DESTROYING: 'destroying',
  /** Site torn down (Pages project + DNS removed). Content preserved. */
  OFFLINE: 'offline',
  /** Soft-deleted by the user. Audit-only; filtered from all UI. */
  DELETED: 'deleted',
} as const);

/** Type derived from SITE_STATUS values */
export type SiteStatus = typeof SITE_STATUS[keyof typeof SITE_STATUS];

/** Array of all valid site statuses (matches Postgres enum). */
export const ALL_SITE_STATUSES = Object.values(SITE_STATUS);

/** Statuses that represent in-flight workflow activity. UI polls while in one of these. */
export const TRANSITIONAL_STATUSES: SiteStatus[] = [
  SITE_STATUS.PUBLISHING,
  SITE_STATUS.DESTROYING,
];

export const isTransitionalStatus = (status: string): boolean =>
  (TRANSITIONAL_STATUSES as string[]).includes(status);

/**
 * Polling cadence (ms) for the sites list while any visible row is in a
 * transitional status. Mirrors the prior hardcoded 10s `setInterval` from
 * the pre-tanstack `useSites` hook.
 */
export const SITES_LIST_POLL_MS = 10_000;
